import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search, Trash2, Plus, FileText, Loader2, Info, Copy, Eye, Send, Pencil,
  Printer, Clock, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight,
  MoreHorizontal, Download, Filter, ListFilter,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { API, axios, formatIcelandicNumber } from "@/utils/constants";

/* ─── Helpers ─── */
const fmtNum = (v) => formatIcelandicNumber(Math.round(v || 0));
const fmtDate = (d) => {
  if (!d) return "";
  const parts = d.slice(0, 10).split("-");
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const calcLine = (l) => {
  const base = parseFloat(l.upphaed_an_vsk) || 0;
  const disc = parseFloat(l.afslattr_pct) || 0;
  const vsk = parseFloat(l.vsk_pct) || 24;
  const magn = parseInt(l.magn) || 1;
  const afterDisc = base * magn * (1 - disc / 100);
  return { ...l, magn, samtals_an_vsk: afterDisc, samtals_med_vsk: afterDisc * (1 + vsk / 100) };
};

const emptyLine = { flokkun: "1000", lysing: "", upphaed_an_vsk: 0, afslattr_pct: 0, vsk_pct: 24, samtals_an_vsk: 0, samtals_med_vsk: 0, magn: 1 };
const emptyForm = {
  kennitala: "", name: "", email: "", tengiliður: "", fastanumer: "", heimilisfang: "", postfang: "",
  bokunardagur: todayISO(), gjalddagi: todayISO(), eindagi: "",
  lines: [{ ...emptyLine }], expenses: [],
};

const ICELANDIC_MONTHS = ["Jan", "Feb", "Mar", "Apr", "Maí", "Jún", "Júl", "Ágú", "Sep", "Okt", "Nóv", "Des"];

const STATUS_MAP = {
  paid: { label: "Greitt", bg: "bg-emerald-500", text: "text-white" },
  sent: { label: "Ógreitt", bg: "bg-amber-400", text: "text-white" },
  draft: { label: "Óútbúið", bg: "bg-blue-500", text: "text-white" },
  credit: { label: "Kreditfærður", bg: "bg-purple-500", text: "text-white" },
  cancelled: { label: "Afturkallað", bg: "bg-gray-400", text: "text-white" },
};

const ITEMS_PER_PAGE = 10;

/* ─── Mini Donut ─── */
const MiniDonut = ({ pct, size = 52 }) => {
  const sw = 5;
  const r = (size - sw) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={sw} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#22c55e" strokeWidth={sw}
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" className="transition-all duration-500" />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        className="fill-emerald-600 font-semibold" fontSize="12"
        transform={`rotate(90 ${size / 2} ${size / 2})`}>{pct}%</text>
    </svg>
  );
};

/* ─── KPI Card ─── */
const KpiCard = ({ title, value, subtitle, icon, testId }) => (
  <div className="rounded-2xl border border-gray-200/60 bg-white px-6 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] flex items-start justify-between" data-testid={testId}>
    <div className="flex flex-col gap-1">
      <span className="text-[13px] text-gray-500 font-medium tracking-wide">{title}</span>
      <span className="text-[28px] font-bold text-gray-900 leading-tight">{value}</span>
      <span className="text-xs text-gray-400 mt-0.5">{subtitle}</span>
    </div>
    <div className="shrink-0 mt-1">{icon}</div>
  </div>
);

/* ─── Chart Tooltip ─── */
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((e, i) => (
        <p key={i} style={{ color: e.color }}>{e.name}: {fmtNum(e.value)} kr.</p>
      ))}
    </div>
  );
};

/* ================================================================ */
export const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("12");
  const [editInvoice, setEditInvoice] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showNew, setShowNew] = useState(false);
  const [sideTab, setSideTab] = useState("stillingar");
  const [athugasemdir, setAthugasemdir] = useState("");
  const [emailToggle, setEmailToggle] = useState(false);
  const [xmlToggle, setXmlToggle] = useState(false);
  const [paydayProducts, setPaydayProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  /* ─── Fetch ─── */
  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payday/invoices`);
      setInvoices(res.data.invoices || []);
    } catch {
      try {
        const res = await axios.get(`${API}/invoices`);
        setInvoices(res.data);
      } catch { toast.error("Villa við að sækja reikninga"); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchInvoices();
    axios.get(`${API}/payday/products`).then(res => {
      if (res.data.success) setPaydayProducts(res.data.products);
    }).catch(() => {});
  }, [fetchInvoices]);

  /* ─── Filtered + paginated ─── */
  const filtered = useMemo(() => {
    const months = parseInt(periodFilter);
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);

    return invoices.filter(inv => {
      const d = new Date(inv.created_at || inv.utgafudagur);
      if (d < cutoff) return false;
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (inv.name || "").toLowerCase().includes(q) ||
          (inv.kennitala || "").includes(q) ||
          String(inv.payday_invoice_number || "").includes(q) ||
          (inv.invoice_number || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [invoices, search, statusFilter, periodFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [search, statusFilter, periodFilter]);

  /* ─── KPI Stats ─── */
  const kpi = useMemo(() => {
    const paid = filtered.filter(i => i.status === "paid");
    const sent = filtered.filter(i => i.status === "sent");
    const billable = paid.length + sent.length;
    const collectionRate = billable > 0 ? Math.round((paid.length / billable) * 100) : 100;
    const avgDays = paid.length > 0 ? Math.round(paid.reduce((s, inv) => {
      const c = new Date(inv.created_at);
      const u = new Date(inv.updated_at || inv.created_at);
      return s + Math.max(1, Math.round((u - c) / 86400000));
    }, 0) / paid.length) : 0;
    const unpaidTotal = sent.reduce((s, i) => s + (i.samtals_med_vsk || i["heildarupphæð"] || 0), 0);
    const paidTotal = paid.reduce((s, i) => s + (i.samtals_med_vsk || i["heildarupphæð"] || 0), 0);
    return { collectionRate, avgDays, unpaidTotal, paidTotal };
  }, [filtered]);

  /* ─── Chart Data ─── */
  const chartData = useMemo(() => {
    const now = new Date();
    const months = parseInt(periodFilter);
    const data = [];
    for (let i = months - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      data.push({
        label: `${ICELANDIC_MONTHS[d.getMonth()]} ${d.getFullYear()}`,
        key, paid: 0, sent: 0, total: 0, count: 0,
      });
    }
    invoices.forEach(inv => {
      const dateStr = (inv.utgafudagur || inv.created_at || "").slice(0, 7);
      const entry = data.find(d => d.key === dateStr);
      if (!entry) return;
      const amt = inv.samtals_med_vsk || inv["heildarupphæð"] || 0;
      entry.total += amt;
      entry.count++;
      if (inv.status === "paid") entry.paid += amt;
      else if (inv.status === "sent") entry.sent += amt;
    });
    return data.map(d => ({ ...d, paid: Math.round(d.paid / 1000), sent: Math.round(d.sent / 1000), total: Math.round(d.total / 1000) }));
  }, [invoices, periodFilter]);

  /* ─── Handlers ─── */
  const selectProduct = (lineIndex, productId) => {
    const product = paydayProducts.find(p => p.id === productId);
    if (!product) return;
    const newLines = [...form.lines];
    newLines[lineIndex] = calcLine({
      ...newLines[lineIndex], lysing: product.name,
      upphaed_an_vsk: product.price_excl_vat, vsk_pct: product.vat_pct || 24,
      payday_product_id: product.id,
    });
    setForm(p => ({ ...p, lines: newLines }));
  };

  const handleDelete = async (e, inv) => {
    e.stopPropagation();
    if (!window.confirm(`Eyða reikningi ${inv.invoice_number}?`)) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`);
      toast.success("Reikningi eytt");
      fetchInvoices();
    } catch { toast.error("Villa við eyðingu"); }
  };

  const handleIssue = async (e, inv) => {
    e.stopPropagation();
    if (!window.confirm(`Útgefa reikning ${inv.invoice_number || "Óútbúið"}? Reikningurinn verður sendur.`)) return;
    try {
      const res = await axios.post(`${API}/payday/invoices/${inv.id}/issue`);
      if (res.data.success) { toast.success(res.data.message || "Reikningur útgefinn"); fetchInvoices(); }
      else { toast.error(res.data.message || "Villa við útgáfu"); }
    } catch (err) { toast.error(err.response?.data?.detail || "Villa við útgáfu"); }
  };

  const openEdit = (inv) => {
    setForm({
      kennitala: inv.kennitala || "", name: inv.name || "", email: inv.email || "",
      tengiliður: inv.tengiliður || "", fastanumer: inv.fastanumer || "",
      heimilisfang: inv.heimilisfang || "", postfang: inv.postfang || "",
      bokunardagur: (inv.utgafudagur || "").slice(0, 10) || todayISO(),
      gjalddagi: (inv.eindagi || inv.utgafudagur || "").slice(0, 10) || todayISO(),
      eindagi: (inv.eindagi || "").slice(0, 10) || "",
      lines: (inv.lines || []).length > 0 ? inv.lines : [{ ...emptyLine }],
      expenses: inv.expenses || [],
    });
    setEditInvoice(inv);
  };

  const openNew = () => {
    setForm({ ...emptyForm, bokunardagur: todayISO(), gjalddagi: todayISO(), eindagi: "", lines: [{ ...emptyLine }], expenses: [] });
    setAthugasemdir("");
    setShowNew(true);
  };

  const updateLine = (idx, field, val) => {
    const updated = form.lines.map((l, i) => i === idx ? calcLine({ ...l, [field]: val }) : l);
    setForm(prev => ({ ...prev, lines: updated }));
  };
  const addLine = () => setForm(prev => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }));
  const removeLine = (idx) => setForm(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));

  const totals = useMemo(() => {
    const sumAn = form.lines.reduce((s, l) => s + (l.samtals_an_vsk || 0), 0);
    const sumMed = form.lines.reduce((s, l) => s + (l.samtals_med_vsk || 0), 0);
    const sumExp = form.expenses.reduce((s, e) => s + (parseFloat(e.upphaed) || 0), 0);
    return { sumAn, sumMed, vsk: sumMed - sumAn, sumExp, total: sumMed + sumExp };
  }, [form.lines, form.expenses]);

  const handleSave = async () => {
    if (!form.kennitala || !form.name) { toast.error("Kennitala og nafn eru nauðsynleg"); return; }
    if (form.lines.length === 0) { toast.error("Bættu við a.m.k. einni reikningslínu"); return; }
    setSaving(true);
    try {
      if (editInvoice) {
        await axios.put(`${API}/invoices/${editInvoice.id}`, form);
        toast.success("Reikningur uppfærður");
        setEditInvoice(null);
      } else {
        const payload = {
          kennitala: form.kennitala, name: form.name, email: form.email,
          phone: form.tengiliður, heimilisfang: form.heimilisfang, postfang: form.postfang,
          bokunardagur: form.bokunardagur, gjalddagi: form.gjalddagi,
          eindagi: form.eindagi || form.gjalddagi, tilvisun: form.fastanumer,
          athugasemdir, gjaldmidill: "ISK", senda_email: emailToggle, krafa_i_netbanka: true,
          lines: form.lines.map(l => ({
            lysing: l.lysing || "", magn: parseInt(l.magn) || 1,
            upphaed_an_vsk: parseFloat(l.upphaed_an_vsk) || 0,
            vsk_pct: parseFloat(l.vsk_pct) || 24, afslattr_pct: parseFloat(l.afslattr_pct) || 0,
            payday_product_id: l.payday_product_id || null,
          })),
        };
        const res = await axios.post(`${API}/payday/invoices/create`, payload);
        if (res.data.success) { toast.success(res.data.message || "Reikningur stofnaður í Payday"); }
        else { toast.error(res.data.message || "Villa"); setSaving(false); return; }
        setShowNew(false);
      }
      setForm(emptyForm);
      setAthugasemdir("");
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || e.response?.data?.message || "Villa"); }
    finally { setSaving(false); }
  };

  const lookupKt = async () => {
    if (form.kennitala.length < 6) return;
    try {
      const res = await axios.get(`${API}/customers`, { params: { q: form.kennitala } });
      const match = res.data.find(c => c.kennitala === form.kennitala);
      if (match) {
        setForm(prev => ({ ...prev, name: match.name || prev.name, heimilisfang: match.address || prev.heimilisfang,
          postfang: `${match.postal_code || ""} ${match.city || ""}`.trim(), email: match.email || prev.email }));
        toast.success("Viðskiptavinur fundinn"); return;
      }
    } catch {}
    try {
      const res = await axios.get(`${API}/payday/fetch-customer/${form.kennitala}`);
      if (res.data.success && res.data.customer) {
        const c = res.data.customer;
        setForm(prev => ({ ...prev, name: c.name || prev.name, heimilisfang: c.address || prev.heimilisfang,
          postfang: `${c.zipCode || ""} ${c.city || ""}`.trim(), email: c.email || prev.email,
          tengiliður: c.phone || prev.tengiliður }));
        toast.success("Viðskiptavinur fundinn í Payday"); return;
      }
    } catch {}
    toast.info("Viðskiptavinur fannst ekki");
  };

  /* ─── Invoice Form Dialog ─── */
  if (showNew || editInvoice) {
    const isEdit = !!editInvoice;
    return (
      <Dialog open={true} onOpenChange={() => { setShowNew(false); setEditInvoice(null); }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0">
          <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-xl font-light text-gray-600">
                Reikningar <span className="text-gray-400 mx-1">&gt;</span>
                <span className="text-gray-900 font-normal">{isEdit ? editInvoice.invoice_number : "Nýr reikningur"}</span>
              </DialogTitle>
              {(() => { const s = isEdit ? STATUS_MAP[editInvoice.status] : STATUS_MAP.draft;
                return <span className={`${s?.bg} ${s?.text} px-2.5 py-0.5 rounded-full text-[10px] font-semibold`}>{s?.label}</span>;
              })()}
            </div>
          </div>
          <div className="flex gap-5 px-6 py-5">
            <div className="flex-1 min-w-0 space-y-5">
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-gray-700">Viðskiptavinur</Label>
                    <button className="text-xs text-teal-600 hover:underline">Nýr viðskiptavinur</button>
                  </div>
                  <div className="flex gap-1">
                    <Input className="h-8 text-sm flex-1" value={form.kennitala}
                      onChange={e => setForm(p => ({ ...p, kennitala: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                      onBlur={lookupKt} placeholder="Kennitala" data-testid="inv-kennitala" />
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400" onClick={lookupKt}>
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                  {form.name && (
                    <div className="text-xs text-gray-600 border-l-2 border-gray-200 pl-3 py-1">
                      <div className="font-medium">{form.name}</div>
                      {form.heimilisfang && <div>{form.heimilisfang}</div>}
                      {form.postfang && <div>{form.postfang}</div>}
                      <div>Kt. {form.kennitala}</div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Bókunardagur", key: "bokunardagur", testId: "inv-bokunardagur" },
                    { label: "Gjalddagi", key: "gjalddagi", testId: "inv-gjalddagi" },
                    { label: "Eindagi", key: "eindagi", testId: "inv-eindagi" },
                  ].map(f => (
                    <div key={f.key} className="flex items-center justify-end gap-2">
                      <Label className="text-xs text-gray-600 w-24 text-right">{f.label}</Label>
                      <Input type="date" className="h-8 text-xs w-36" value={form[f.key]}
                        onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} data-testid={f.testId} />
                    </div>
                  ))}
                  <div className="flex items-center justify-end gap-2">
                    <Label className="text-xs text-gray-600 w-24 text-right">Tilvísun</Label>
                    <Input className="h-8 text-xs w-36" value={form.fastanumer}
                      onChange={e => setForm(p => ({ ...p, fastanumer: e.target.value }))} data-testid="inv-tilvisun" />
                  </div>
                </div>
              </div>

              {/* Lines Table */}
              <div>
                <div className="grid grid-cols-[2fr_0.5fr_0.7fr_0.7fr_0.5fr_0.4fr_0.8fr_0.8fr_auto] gap-1 text-[9px] text-gray-500 font-bold uppercase tracking-wider pb-1 border-b border-gray-200">
                  <span>Lýsing</span><span>Magn</span><span>Án VSK</span><span>Með VSK</span>
                  <span>VSK %</span><span>Afsl.%</span><span className="text-right">Samtals án VSK</span><span className="text-right">Samtals m. VSK</span><span></span>
                </div>
                {form.lines.map((line, i) => (
                  <div key={i} className="grid grid-cols-[2fr_0.5fr_0.7fr_0.7fr_0.5fr_0.4fr_0.8fr_0.8fr_auto] gap-1 items-center py-1.5 border-b border-gray-50" data-testid={`inv-line-${i}`}>
                    <Select value={line.payday_product_id || "__custom"} onValueChange={v => {
                      if (v === "__custom") { const nl = [...form.lines]; nl[i] = { ...nl[i], payday_product_id: undefined }; setForm(p => ({ ...p, lines: nl })); }
                      else { selectProduct(i, v); }
                    }}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`inv-line-${i}-product`}><SelectValue placeholder="Veldu vöru..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__custom" className="text-xs italic text-gray-400">Frjáls texti...</SelectItem>
                        {paydayProducts.map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name} — {fmtNum(p.price_excl_vat)} kr.</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="h-7 text-xs text-right" type="number" value={line.magn || 1} onChange={e => updateLine(i, "magn", parseInt(e.target.value) || 1)} />
                    <Input className="h-7 text-xs text-right" type="number" value={line.upphaed_an_vsk || ""} onChange={e => updateLine(i, "upphaed_an_vsk", parseFloat(e.target.value) || 0)} />
                    <span className="text-[10px] text-right text-gray-400 tabular-nums">{fmtNum(line.samtals_med_vsk)}</span>
                    <Select value={String(line.vsk_pct)} onValueChange={v => updateLine(i, "vsk_pct", parseInt(v))}>
                      <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="24">24%</SelectItem><SelectItem value="11">11%</SelectItem><SelectItem value="0">0%</SelectItem></SelectContent>
                    </Select>
                    <Input className="h-7 text-xs text-right" type="number" value={line.afslattr_pct || ""} onChange={e => updateLine(i, "afslattr_pct", parseFloat(e.target.value) || 0)} />
                    <span className="text-[10px] text-right text-gray-600 tabular-nums">{fmtNum(line.samtals_an_vsk)}</span>
                    <span className="text-[10px] text-right font-medium tabular-nums">{fmtNum(line.samtals_med_vsk)}</span>
                    <button className="p-0.5 text-red-300 hover:text-red-500" onClick={() => removeLine(i)}><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
                <button onClick={addLine} className="w-full mt-1 border border-dashed border-gray-300 rounded py-1.5 text-[10px] text-gray-400 hover:text-gray-600" data-testid="add-line-btn">+ Bæta við línu</button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-semibold text-gray-700">Athugasemdir</Label>
                  <span className="text-[10px] text-gray-400">{1000 - athugasemdir.length} stafir eftir</span>
                </div>
                <textarea className="w-full border border-gray-200 rounded p-2 text-xs text-gray-600 resize-none h-16 focus:outline-none focus:ring-1 focus:ring-blue-200"
                  value={athugasemdir} onChange={e => setAthugasemdir(e.target.value.slice(0, 1000))} placeholder="Sláðu inn athugasemdir..." data-testid="inv-comments" />
              </div>

              <div className="flex items-center justify-center gap-4 py-2">
                <div className="text-center"><div className="text-[9px] text-gray-400 uppercase">Án VSK</div><div className="text-lg font-semibold text-gray-700 tabular-nums">{fmtNum(totals.sumAn)} kr.</div></div>
                <span className="text-lg text-gray-300">+</span>
                <div className="text-center"><div className="text-[9px] text-gray-400 uppercase">VSK</div><div className="text-lg font-semibold text-gray-700 tabular-nums">{fmtNum(totals.vsk)} kr.</div></div>
                <span className="text-lg text-gray-300">=</span>
                <div className="text-center"><div className="text-[9px] text-gray-400 uppercase">Samtals m. VSK</div><div className="text-lg font-bold text-gray-900 tabular-nums">{fmtNum(totals.total)} kr.</div></div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-60 shrink-0">
              <div className="border border-gray-200 rounded-lg bg-white">
                <div className="flex border-b border-gray-200">
                  <button onClick={() => setSideTab("stillingar")} className={`flex-1 py-2.5 text-xs font-medium text-center ${sideTab === "stillingar" ? "text-gray-800 border-b-2 border-green-500" : "text-gray-400"}`} data-testid="tab-stillingar">Stillingar</button>
                  <button onClick={() => setSideTab("adgerdir")} className={`flex-1 py-2.5 text-xs font-medium text-center ${sideTab === "adgerdir" ? "text-gray-800 border-b-2 border-green-500" : "text-gray-400"}`} data-testid="tab-adgerdir">Aðgerðir</button>
                </div>
                <div className="p-4">
                  {sideTab === "stillingar" ? (
                    <div className="space-y-4" data-testid="sidebar-stillingar">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Label className="text-xs font-semibold text-gray-700">Tölvupóstur</Label>
                          <button onClick={() => setEmailToggle(!emailToggle)} className={`relative inline-flex h-4 w-8 shrink-0 rounded-full transition-colors ${emailToggle ? "bg-green-500" : "bg-red-400"}`}>
                            <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform mt-0.5 ${emailToggle ? "translate-x-4" : "translate-x-0.5"}`} />
                          </button>
                          <span className={`text-[9px] font-bold ${emailToggle ? "text-green-600" : "text-red-500"}`}>{emailToggle ? "JÁ" : "NEI"}</span>
                        </div>
                        <Input className="h-7 text-[10px] mt-1" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="netfang@example.is" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-700 mb-1 block">Gjaldmiðill</Label>
                        <Select defaultValue="ISK"><SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="ISK">Íslensk króna (ISK)</SelectItem><SelectItem value="EUR">Evra (EUR)</SelectItem></SelectContent></Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs font-semibold text-gray-700">Rafrænn reikningur (XML)</Label>
                        <button onClick={() => setXmlToggle(!xmlToggle)} className={`relative inline-flex h-4 w-8 shrink-0 rounded-full transition-colors ${xmlToggle ? "bg-green-500" : "bg-red-400"}`}>
                          <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform mt-0.5 ${xmlToggle ? "translate-x-4" : "translate-x-0.5"}`} />
                        </button>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-700 mb-1 block">Greiðslumáti</Label>
                        <div className="space-y-1">
                          <label className="flex items-center gap-2 p-1.5 rounded border border-gray-200 cursor-pointer hover:bg-gray-50 text-[10px]">
                            <input type="radio" name="payment" defaultChecked className="accent-teal-600 h-3 w-3" /><span>Greiddur</span></label>
                          <label className="flex items-center gap-2 p-1.5 rounded border border-gray-200 cursor-pointer hover:bg-gray-50 text-[10px]">
                            <input type="radio" name="payment" className="accent-teal-600 h-3 w-3" /><span>Krafa í netbanka</span></label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-0.5" data-testid="sidebar-adgerdir">
                      <button className="w-full flex items-center gap-2 px-2 py-2.5 rounded text-xs text-gray-700 hover:bg-gray-50"><Printer className="h-3.5 w-3.5 text-gray-400" />Prenta drög</button>
                      <button className="w-full flex items-center gap-2 px-2 py-2.5 rounded text-xs text-gray-700 hover:bg-gray-50"><Eye className="h-3.5 w-3.5 text-gray-400" />Skoða drög</button>
                      <button className="w-full flex items-center gap-2 px-2 py-2.5 rounded text-xs text-red-500 hover:bg-red-50"
                        onClick={() => { if (isEdit) { handleDelete({ stopPropagation: () => {} }, editInvoice); setEditInvoice(null); } else { setShowNew(false); } }}>
                        <Trash2 className="h-3.5 w-3.5" />Eyða reikningi</button>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={handleSave} disabled={saving}
                className="w-full mt-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white text-sm font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="inv-save-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? "Stofnar..." : (isEdit ? "Vista reikning" : "Stofna reikning")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  /* ─── Loading State ─── */
  if (loading) return (
    <div className="p-8 text-sm text-gray-500 flex items-center gap-2" data-testid="invoices-loading">
      <Loader2 className="h-4 w-4 animate-spin" />Hleður reikningum...
    </div>
  );

  /* ─── Main Page ─── */
  return (
    <div className="p-6 lg:p-8 bg-[#f0f1f3] min-h-screen" data-testid="invoices-page">
      <div className="max-w-7xl mx-auto space-y-5">

        {/* ─── Header ─── */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Reikningar</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="h-9 text-xs gap-1.5 border-gray-300 text-gray-600 bg-white shadow-sm">
              <Download className="h-3.5 w-3.5" />Flytja út
            </Button>
            <Button variant="outline" className="h-9 w-9 p-0 border-gray-300 text-gray-600 bg-white shadow-sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            <Button onClick={openNew} className="h-9 text-xs gap-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-testid="new-invoice-btn">
              <Plus className="h-3.5 w-3.5" />Nýr reikningur
            </Button>
          </div>
        </div>

        {/* ─── KPI Cards ─── */}
        <div className="grid grid-cols-4 gap-4" data-testid="invoice-stats">
          <KpiCard testId="collection-rate" title="Innheimtuhlutfall"
            value={`${kpi.collectionRate}%`} subtitle="síðustu 30 dagar"
            icon={<MiniDonut pct={kpi.collectionRate} size={56} />} />
          <KpiCard testId="payment-speed" title="Greiðsluhraði"
            value={`${kpi.avgDays} dagur`} subtitle="meðaltal"
            icon={<div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center"><Clock className="h-5 w-5 text-blue-400" /></div>} />
          <KpiCard testId="unpaid-invoices-card" title="Ógreiddir reikn."
            value={`${fmtNum(kpi.unpaidTotal)} kr.`} subtitle="núverandi staða"
            icon={<div className="h-12 w-12 rounded-full bg-orange-50 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-orange-400" /></div>} />
          <KpiCard testId="paid-invoices-card" title="Greiddir reikn."
            value={`${fmtNum(kpi.paidTotal)} kr.`} subtitle="síðustu 30 dagar"
            icon={<div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center"><CheckCircle className="h-5 w-5 text-emerald-400" /></div>} />
        </div>

        {/* ─── Main Card ─── */}
        <div className="rounded-2xl border border-gray-200/60 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="invoices-main-card">

          {/* Card Header: Title + Filters */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <h2 className="text-xl font-bold text-gray-900">Reikningar</h2>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-[130px] text-xs border-gray-300 bg-white gap-1 rounded-lg shadow-sm" data-testid="status-filter">
                  <ListFilter className="h-3.5 w-3.5 text-gray-400" /><SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">Allar stöður</SelectItem>
                  <SelectItem value="paid" className="text-xs">Greitt</SelectItem>
                  <SelectItem value="sent" className="text-xs">Ógreitt</SelectItem>
                  <SelectItem value="draft" className="text-xs">Óútbúið</SelectItem>
                  <SelectItem value="credit" className="text-xs">Kreditfærður</SelectItem>
                  <SelectItem value="cancelled" className="text-xs">Afturkallað</SelectItem>
                </SelectContent>
              </Select>
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger className="h-9 w-[180px] text-xs border-gray-300 bg-white rounded-lg shadow-sm" data-testid="period-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3" className="text-xs">Síðustu 3 mánuði</SelectItem>
                  <SelectItem value="6" className="text-xs">Síðustu 6 mánuði</SelectItem>
                  <SelectItem value="12" className="text-xs">Síðustu 12 mánuði</SelectItem>
                  <SelectItem value="24" className="text-xs">Síðustu 24 mánuði</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Search + Charts */}
          <div className="px-6 pb-5 space-y-4">
            <div className="relative w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Leita eftir nafni, kennitölu, númer..."
                className="pl-10 h-10 rounded-xl border-gray-300 bg-gray-50/60 text-sm" data-testid="inv-search" />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div className="h-[170px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} width={40} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTip />} />
                      <Area type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} fill="url(#colorTotal)" dot={{ r: 3, fill: '#3b82f6' }} name="Heildar" />
                      <Area type="monotone" dataKey="paid" stroke="#22c55e" strokeWidth={2} fill="url(#colorPaid)" dot={{ r: 3, fill: '#22c55e' }} name="Greitt" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="h-[170px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.12} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.12} />
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} width={40} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTip />} />
                      <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} fill="url(#colorCount)" dot={{ r: 3, fill: '#3b82f6' }} name="Fjöldi" />
                      <Area type="monotone" dataKey="sent" stroke="#f59e0b" strokeWidth={2} fill="url(#colorSent)" dot={{ r: 3, fill: '#f59e0b' }} name="Ógreitt" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

          {/* Table */}
          {filtered.length === 0 ? (
            <div className="px-6 py-14 text-center border-t border-gray-100" data-testid="invoices-empty">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <FileText className="h-5 w-5 text-gray-500" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Engir reikningar fundust</h2>
              <p className="mt-2 text-sm text-gray-500">Smelltu á "Nýr reikningur" til að byrja.</p>
            </div>
          ) : (
            <div data-testid="invoices-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-t border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    <th className="text-left py-3.5 px-6">Viðskiptavinur</th>
                    <th className="text-left py-3.5 px-2 w-20">Tilk/N.</th>
                    <th className="text-left py-3.5 px-2 w-24">Dag.</th>
                    <th className="text-left py-3.5 px-2 w-24">Eindagi</th>
                    <th className="text-left py-3.5 px-2 w-20">Tímabil</th>
                    <th className="text-right py-3.5 px-2 w-28">Upphæð</th>
                    <th className="text-right py-3.5 px-3 w-28">Upphæð</th>
                    <th className="text-center py-3.5 px-2 w-14">Gjaldm.</th>
                    <th className="text-center py-3.5 px-2 w-24">Staða</th>
                    <th className="text-center py-3.5 px-2 w-24">Staða</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(inv => {
                    const st = STATUS_MAP[inv.status] || STATUS_MAP.draft;
                    return (
                      <tr key={inv.id} className="border-b border-gray-100 hover:bg-blue-50/30 cursor-pointer group transition-colors"
                        onClick={() => openEdit(inv)} data-testid={`inv-row-${inv.id}`}>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="h-2 w-2 rounded-full bg-gray-300 shrink-0" />
                            <span className="text-sm font-medium text-gray-900">{inv.name || <span className="italic text-gray-400">Enginn viðskiptavinur</span>}</span>
                          </div>
                        </td>
                        <td className="py-4 px-2 text-xs text-gray-400 italic">{inv.fastanumer || "Ekki sett"}</td>
                        <td className="py-4 px-2 text-xs text-gray-600">{fmtDate(inv.utgafudagur)}</td>
                        <td className="py-4 px-2 text-xs text-gray-400">{fmtDate(inv.eindagi)}</td>
                        <td className="py-4 px-2 text-xs text-gray-400">—</td>
                        <td className="py-4 px-2 text-right text-sm font-semibold text-gray-800 tabular-nums">
                          {fmtNum(inv.samtals_an_vsk || 0)} kr.
                        </td>
                        <td className="py-4 px-3 text-right text-sm font-bold text-gray-900 tabular-nums">
                          {fmtNum(inv.samtals_med_vsk || inv["heildarupphæð"] || 0)} kr.
                        </td>
                        <td className="py-4 px-2 text-center text-xs text-gray-500">ISK</td>
                        <td className="py-4 px-2 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-semibold ${st.bg} ${st.text}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="py-4 px-2" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-0.5">
                            {inv.status === "draft" && (
                              <button className="p-1.5 rounded-md hover:bg-emerald-100 transition-colors" title="Útgefa" onClick={e => handleIssue(e, inv)} data-testid={`inv-issue-${inv.id}`}>
                                <Send className="h-3.5 w-3.5 text-emerald-600" />
                              </button>
                            )}
                            <button className="p-1.5 rounded-md hover:bg-gray-100 transition-colors" title="Skoða" onClick={() => openEdit(inv)}>
                              <Eye className="h-3.5 w-3.5 text-gray-400" />
                            </button>
                            <button className="p-1.5 rounded-md hover:bg-gray-100 transition-colors" title="Breyta">
                              <Pencil className="h-3.5 w-3.5 text-gray-400" />
                            </button>
                            <button className="p-1.5 rounded-md hover:bg-red-50 transition-colors" title="Eyða" onClick={e => handleDelete(e, inv)}>
                              <Trash2 className="h-3.5 w-3.5 text-gray-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100" data-testid="invoices-pagination">
                <span className="text-xs text-gray-500">Samtals <span className="font-semibold text-gray-700">{filtered.length}</span></span>
                <span className="text-xs text-gray-500">
                  {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} af {filtered.length}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} af {filtered.length}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
                      className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                      <ChevronLeft className="h-4 w-4 text-gray-500" />
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) { page = i + 1; }
                      else if (currentPage <= 3) { page = i + 1; }
                      else if (currentPage >= totalPages - 2) { page = totalPages - 4 + i; }
                      else { page = currentPage - 2 + i; }
                      return (
                        <button key={page} onClick={() => setCurrentPage(page)}
                          className={`h-8 w-8 rounded-lg text-xs font-medium transition-colors ${page === currentPage ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"}`}>
                          {page}
                        </button>
                      );
                    })}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                      className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Invoices;
